import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, CheckCircle, Zap, Shield, DollarSign } from 'lucide-react';
import { useSalesTracking } from '../hooks/useSalesTracking';
import { usePageTitle } from '../hooks/usePageTitle';
import { trackProductPageView } from '../utils/analytics';
import { APP_NAME } from '../config/branding';

import IsometricOnlineBooking from '../components/IsometricOnlineBooking';

const OnlineBooking: React.FC = () => {
  usePageTitle('Online Booking for Veterinary Practices - 24/7 Scheduling');

  // Track sales link clicks
  useSalesTracking();

  useEffect(() => {
    trackProductPageView('online_booking');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Notion Style */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                Online Booking
              </span>
            </div>

            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Accept appointments 24/7
            </h1>

            <p className="text-xl text-gray-600 mb-8">
              Let pet owners book appointments directly from your <Link to="/products/website-hosting" className="text-blue-600 hover:text-blue-700 underline">professional website</Link>, anytime. Real-time availability, automatic confirmations, and seamless integration with your practice calendar. Works perfectly with our <Link to="/products/ai-front-desk" className="text-blue-600 hover:text-blue-700 underline">24/7 AI Front Desk</Link> for complete appointment automation.
            </p>

            <div className="flex gap-4 mb-8">
              <Link
                to="/pricing"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                See pricing
              </Link>
              <Link
                to="/contact"
                className="bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Request demo →
              </Link>
            </div>
          </div>

          <div className="relative h-[400px] w-full flex items-center justify-center bg-white rounded-3xl overflow-hidden">
            <IsometricOnlineBooking />
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Always Available</h3>
              <p className="text-sm text-gray-600">Accept bookings after hours and on weekends when your phone lines are closed.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Instant Confirmation</h3>
              <p className="text-sm text-gray-600">Appointments are confirmed immediately and synced to your calendar in real-time.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Generate Revenue</h3>
              <p className="text-sm text-gray-600">Capture appointments you'd otherwise miss from after-hours requests.</p>
            </div>
          </div>
        </div>

        {/* Embeddable Widget Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple integration</h2>
          <p className="text-lg text-gray-600 mb-8">
            Add a single code snippet to your website and you're live. Works with any website platform - WordPress, Wix, Squarespace, or custom sites.
          </p>

          <div className="bg-gray-50 rounded-lg p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">One-time setup:</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Copy the embed code from your dashboard</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Paste it into your website's HTML</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Widget appears instantly - that's it!</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg p-6">
                <pre className="text-xs text-green-400 font-mono overflow-x-auto">
                  <code>{`<script src="helppet-widget.js">
</script>
<script>
  HelpPetWidget.init();
</script>`}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>

        {/* Real-Time Availability */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Real-time availability</h2>
          <p className="text-lg text-gray-600 mb-8">
            Pet owners see your actual available time slots. No more double-bookings, no more phone tag. They pick a time that works, and it's automatically added to your schedule.
          </p>

          <div className="bg-gray-50 rounded-lg p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Shows only your actual open time slots</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Updates instantly when appointments are booked</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Respects your work schedule and time off</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Prevents overbooking automatically</span>
                </div>
              </div>

              <div className="flex justify-center">
                <img
                  src="/month.jpg"
                  alt="Monthly calendar view showing available appointment slots"
                  className="rounded-lg shadow-lg"
                  style={{ maxWidth: '450px', height: 'auto', objectFit: 'contain' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Customizable Rules */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Your rules, your schedule</h2>
          <p className="text-lg text-gray-600 mb-8">
            Control exactly when and how pet owners can book. Set appointment types, durations, buffer times, and availability - all from your dashboard.
          </p>

          <div className="bg-gray-50 rounded-lg p-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Multiple appointment types</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Custom appointment durations</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Buffer time between appointments</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Block out lunch and breaks</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Set advance booking limits</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Multiple veterinarians supported</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple pricing</h2>
          <p className="text-lg text-gray-600 mb-8">
            Online booking is included in our plans. See plan details on the Pricing page.
          </p>

          <div className="max-w-md">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <div className="text-center mb-6">
                <div className="text-2xl font-bold text-gray-900 mb-2">Included with plans</div>
                <div className="text-gray-600">Essential • Professional • Enterprise</div>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Embeddable booking widget</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Real-time availability</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Unlimited appointments</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Email confirmations</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Calendar sync</span>
                </div>
              </div>

              <Link
                to="/pricing"
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors text-center block"
              >
                View pricing
              </Link>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="border-t border-gray-200 pt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Benefits for everyone</h2>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">For Your Practice</h3>
              <div className="space-y-3 text-gray-600">
                <p>• Reduce phone call volume</p>
                <p>• Capture after-hours appointments</p>
                <p>• Eliminate scheduling back-and-forth</p>
                <p>• Fewer no-shows with confirmations</p>
                <p>• Staff focuses on patient care</p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">For Pet Owners</h3>
              <div className="space-y-3 text-gray-600">
                <p>• Book anytime, anywhere</p>
                <p>• See available times immediately</p>
                <p>• No waiting on hold</p>
                <p>• Instant confirmation email</p>
                <p>• Easy to reschedule</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineBooking;
